/* Shared task dispatcher. Fund sign-off always uses the dated Fund Overview;
 * object decisions retain the existing focused panes. */

import { Navigate } from 'react-router-dom'
import ChainReviewPage from '../pages/ChainReviewPage'
import ReconcilePane from './ReconcilePane'
import ChasePane from './ChasePane'
import TakeOverPane from './TakeOverPane'
import ResolvePane from './ResolvePane'
import TradeDuplicatePane from './TradeDuplicatePane'
import EvidenceApprovalPane from './EvidenceApprovalPane'
import SubchainClosePane from './evidence/SubchainClosePane'
import UnroutablePane from './UnroutablePane'
import UnlockedCopyPane from './UnlockedCopyPane'
import { RESOLVE_PANE_TASK_TYPES } from './resolvePaneTaskTypes'
import { isReconTaskType, type TaskQueueItem } from '../lib/tasks'
import { RkDeckEmbed } from './rkdecks/RkDecksSurface'
import { CHASE_PANE_TASK_TYPES, TAKEOVER_PANE_TASK_TYPES } from './taskPaneTypes'
import { ErrorState } from './state'

export interface TaskPaneProps {
  task: TaskQueueItem
  /** RK W4 recon-fold: with the flag on a document-less recon task
   *  renders as its reconcile-only DECK instead of ReconcilePane. */
  rkDecksOn: boolean | null
  onResolved?: () => void
}

const noop = () => {}

function FundSignoffPane({ task }: { task: TaskQueueItem }) {
  const fundId = task.target_type === 'fund' ? task.target_id : task.fund_id
  return fundId ? (
    <Navigate replace to={`/v2/funds/${encodeURIComponent(fundId)}`} />
  ) : (
    <ErrorState what="fund sign-off" error={new Error('The task’s fund is unavailable.')} />
  )
}

export default function TaskPane({ task, rkDecksOn, onResolved: cb }: TaskPaneProps) {
  const onResolved = cb ?? noop
  if (task.task_type === 'verification_signoff') return <FundSignoffPane task={task} />
  if (
    task.task_type === 'evidence_approval' &&
    (task.task_data as Record<string, unknown> | null)?.kind === 'subchain_close'
  )
    return <SubchainClosePane key={task.task_id} task={task} onResolved={onResolved} />
  // A proposal_review TASK is per-chain (target_type='chain') — it routes to the
  // chain-review surface (aggregation + diagram), NOT the per-group ApprovePane.
  if (task.task_type === 'source_question')
    return (
      <Navigate
        replace
        to={`/v2/workbench?scope=all&queue=book&recovery=1&task=${encodeURIComponent(task.task_id)}`}
      />
    )
  if (task.task_type === 'proposal_review')
    return <ChainReviewPage taskId={task.task_id} embedded onResolved={onResolved} />

  if (isReconTaskType(task.task_type))
    return rkDecksOn === true ? (
      <RkDeckEmbed taskId={task.task_id} onResolved={onResolved} />
    ) : (
      <ReconcilePane task={task} onResolved={onResolved} />
    )

  // request_unlocked_copy is an out-of-band chase with its own screen; it
  // used to land on the never-dead-end fallback as a raw enum over JSON.
  if (task.task_type === 'request_unlocked_copy')
    return <UnlockedCopyPane task={task} onResolved={onResolved} />

  if (CHASE_PANE_TASK_TYPES.includes(task.task_type))
    return <ChasePane task={task} onResolved={onResolved} />

  if (TAKEOVER_PANE_TASK_TYPES.includes(task.task_type))
    return <TakeOverPane task={task} onResolved={onResolved} />

  if (RESOLVE_PANE_TASK_TYPES.includes(task.task_type))
    return <ResolvePane task={task} onResolved={onResolved} />

  if (task.task_type === 'trade_duplicate_question')
    return <TradeDuplicatePane task={task} onResolved={onResolved} />

  if (task.task_type === 'evidence_approval')
    return <EvidenceApprovalPane task={task} onResolved={onResolved} />

  /* Never-dead-end fallback (Gap 2): any type without a dedicated pane
     gets a visible card + the generic backend verbs — never a disabled
     skeleton stub. */
  return <UnroutablePane task={task} onResolved={onResolved} />
}
